const DATE_PARTS = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export const browserTimeZone =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Johannesburg";

export const practicalTimeZones = Array.from(
  new Set([
    browserTimeZone,
    "Africa/Johannesburg",
    "Africa/Lagos",
    "Africa/Nairobi",
    "Europe/London",
    "Europe/Paris",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
  ]),
);

export function toDateKey(date: Date, timeZone = browserTimeZone) {
  const formatter =
    timeZone === browserTimeZone
      ? DATE_PARTS
      : new Intl.DateTimeFormat("en-CA", {
          timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
  const values = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return `${values.year}-${values.month}-${values.day}`;
}

export function addDaysToKey(dateKey: string, days: number) {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function dateRangeKeys(numberOfDays: number, timeZone = browserTimeZone) {
  const today = toDateKey(new Date(), timeZone);
  return Array.from({ length: numberOfDays }, (_, index) => addDaysToKey(today, index));
}

export function formatDay(dateKey: string, timeZone = browserTimeZone) {
  const date = new Date(`${dateKey}T12:00:00Z`);
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
}

export function formatFullDateTime(value: string, timeZone = browserTimeZone) {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
    hourCycle: "h23",
  }).format(new Date(value));
}

export function formatTime(value: string, timeZone = browserTimeZone) {
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

export function relativeDayLabel(value: string, timeZone = browserTimeZone) {
  const key = toDateKey(new Date(value), timeZone);
  const today = toDateKey(new Date(), timeZone);
  if (key === today) return "Today";
  if (key === addDaysToKey(today, 1)) return "Tomorrow";
  return formatDay(key, timeZone);
}

export function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} hr ${remainder} min` : `${hours} hr`;
}
